// Typ: FindInscribedFromCentralAngleTask
// Podtemat: Kąty i styczne → Kąt wpisany z podanego kąta środkowego
// Zależność: α_w = (1/2) α_śr

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, {
  parseFractionLikeInput as parseFraction,
} from "../../../../components/inputs/FractionInput";
import CircleAnglesDiagram from "../../../../components/diagrams/CircleAnglesDiagram";


/* ===== helpers ===== */

const DEC_TOL = 0.1; // ±0.1° akceptujemy też wartości niecałkowite
function preprocessRaw(raw) {
  if (raw == null) return "";
  return String(raw).trim().replace(/\u2212/g, "-").replace(/\s+/g, "").replace(",", ".");
}

function pretty(x) {
  const s = String(x ?? "").trim();
  if (!s) return "";
  return s.replace(/\s+/g, "").replace(".", "{,}");
}

/* ===== generator ===== */

export function generateTask() {
  // ładne kąty środkowe, ale max 300°
  const A15 = Array.from({ length: 23 }, (_, i) => 15 * (i + 1));  // 15..345
  const A10 = Array.from({ length: 34 }, (_, i) => 10 * (i + 2));  // 20..350
  const A40 = Array.from({ length: 8  }, (_, i) => 40 * (i + 1));  // 40..320

  const ALL = [...new Set([...A15, ...A10, ...A40])]
    .filter(a => a !== 360 && a <= 300); // ⟵ limit 300°

  const alpha_sr = ALL[Math.floor(Math.random() * ALL.length)];
  const alpha_w  = alpha_sr / 2;

  return { type: "FindInscribedFromCentralAngleTask", data: { alpha_sr, alpha_w } };
}

/* ===== treść ===== */

export function renderTask({ data }) {
  const { alpha_sr } = data;
  return (
    <div style={{ marginTop: 8, marginBottom: 8 }}>
      Dany jest kąt środkowy{" "}
      <InlineMath math={`\\alpha_{\\text{śr}}=${alpha_sr}^{\\circ}`} />.
      Oblicz kąt wpisany oparty na tym samym łuku{" "}
      <span style={{ whiteSpace: "nowrap" }}><InlineMath math={`\\alpha_{w}`} />{"."}</span>
    </div>
  );
}

/* ===== input ===== */

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const val = userAnswer ?? { raw: "" };
  return (
    <FractionInput
      value={val}
      onChange={(v) => onChange(v)}
      disabled={disabled}
      label="α_w"          // nazwa pokaże się w podglądzie i "Twoja odpowiedź"
      suffixLatex="°"      // zwykły znak stopnia — stabilny podgląd
      placeholder="np. 37.5 lub 3/2"
    />
  );
}

/* ===== check ===== */

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer) return false;
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  if (raw == null || String(raw).trim() === "") return false;

  const val = parseFraction(preprocessRaw(raw));
  if (!Number.isFinite(val)) return false;

  const expected = data.alpha_w;
  return Math.abs(val - expected) <= DEC_TOL;
}

/* ===== answer (krótko) ===== */

export function renderAnswer({ data }) {
  const { alpha_w } = data;
  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`\\alpha_{w} = ${alpha_w}^{\\circ}`} />
    </div>
  );
}

/* ===== explanation ===== */

export function renderExplanation({ data }) {
  const { alpha_sr, alpha_w } = data;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div><strong>Krok 1.</strong> Wzór:</div>
      <BlockMath math={'\\alpha_{w} = \\tfrac{1}{2}\\,\\alpha_{\\text{śr}}'} />

      <div><strong>Krok 2.</strong> Podstawienie:</div>
      <BlockMath math={`\\alpha_{w} = \\tfrac{1}{2}\\cdot ${alpha_sr}^{\\circ} = ${alpha_w}^{\\circ}`} />

      {/* podpis z lewej + wycentrowany diagram */}
      <div style={{ marginTop: 12, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
        <div style={{ alignSelf: "stretch", textAlign: "left", color: "currentColor" }}>
          Wizualizacja:
        </div>
        <CircleAnglesDiagram centralDeg={alpha_sr} centralTone="given" inscribedTone="unknown" />
      </div>
    </div>
  );
}

/* ===== user answer preview ===== */

export function renderUserAnswer(userAnswer) {
  if (!userAnswer) return <i>(brak odpowiedzi)</i>;
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`\\displaystyle \\alpha_{w} = ${pretty(raw)}^\\circ`} />
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
